import { ComponentProps, MutableRefObject, useEffect } from 'react'

import { F0Box, F0Form, F0Text } from '@factorialco/f0-react'
import type {
  F0FormRef,
  F0FormSchema,
  F0FormSubmitResult,
  F0SectionConfig,
} from '@factorialco/f0-react'
import { DetailsItemsList, RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import { TrainingRequestDetailFragment } from 'generated/resources/graphql'
import { z } from 'zod'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

type DetailItem = ComponentProps<typeof DetailsItemsList>['details'][number]
type DetailsList = ComponentProps<typeof DetailsItemsList>['details']

/**
 * Builds the header details list shared by both NewProposal and FromCatalog.
 * Includes training type, requester, and (in read-only mode) participants.
 */
export function buildHeaderDetails(
  trainingRequest: TrainingRequestDetailFragment,
  trainingTypeLabel: string,
  options: { isDraft: boolean }
): DetailsList {
  const empty = '-'

  const requester: DetailItem = {
    title: i18n.t('trainings.training_requests.my_requests.detail.requester'),
    content: trainingRequest.author?.employee
      ? {
          type: 'person',
          firstName: trainingRequest.author.employee.fullName,
          lastName: '',
          avatarUrl: trainingRequest.author.employee.avatar?.url
            ? buildUrl(trainingRequest.author.employee.avatar.url)
            : undefined,
        }
      : { type: 'item', text: empty },
  }

  const details: DetailsList = [
    {
      title: i18n.t('trainings.training_requests.my_requests.detail.training_type.title'),
      content: { type: 'item', text: trainingTypeLabel },
    },
    requester,
  ]

  if (!options.isDraft) {
    const participantAvatars = (trainingRequest.participants ?? []).map((p) => ({
      firstName: p.preferredName || p.firstName,
      lastName: p.lastName,
      src: p.avatar?.url ? buildUrl(p.avatar.url) : undefined,
    }))

    if (participantAvatars.length > 0) {
      details.push({
        title: i18n.t('tasks.approvals.trainings_training_request.fields.participants'),
        content: {
          type: 'avatar-list',
          avatarList: {
            type: 'person',
            avatars: participantAvatars,
            size: 'sm',
            max: 5,
          },
        },
      })
    }
  }

  return details
}

/**
 * A labeled rich text display section used in read-only mode.
 */
export const RichTextSection = ({
  label,
  content,
  paddingBottom = 'none',
  paddingTop = 'none',
}: {
  label: string
  content: string
  paddingBottom?: ComponentProps<typeof F0Box>['paddingBottom']
  paddingTop?: ComponentProps<typeof F0Box>['paddingTop']
}) => (
  <F0Box
    display="flex"
    flexDirection="column"
    gap="xl"
    paddingBottom={paddingBottom}
    paddingTop={paddingTop}
  >
    <F0Text variant="label" content={label} />
    <RichTextDisplay content={content || '-'} />
  </F0Box>
)

type FormHookResult<TSchema extends F0FormSchema = F0FormSchema> = {
  schema: TSchema
  defaultValues: Partial<z.infer<TSchema>>
  sections: Record<string, F0SectionConfig>
  onSubmit: (data: z.infer<TSchema>) => Promise<F0FormSubmitResult> | F0FormSubmitResult
  setSendForApproval: (value: boolean) => void
}

/**
 * Generic draft form wrapper used by both NewProposal and FromCatalog.
 * Renders header details + F0Form with the given form hook result.
 */
export const DraftForm = <TSchema extends F0FormSchema>({
  formName,
  formHook,
  headerDetails,
  formRef,
  onFormReady,
}: {
  formName: string
  formHook: FormHookResult<TSchema>
  headerDetails: DetailsList
  formRef?: MutableRefObject<F0FormRef | null>
  onFormReady?: (helpers: { setSendForApproval: (value: boolean) => void }) => void
}) => {
  const { schema, defaultValues, sections, onSubmit, setSendForApproval } = formHook

  useEffect(() => {
    onFormReady?.({ setSendForApproval })
  }, [onFormReady, setSendForApproval])

  return (
    <F0Box display="flex" flexDirection="column" gap="xl">
      <DetailsItemsList details={headerDetails} tableView />
      <F0Form
        formRef={formRef}
        name={formName}
        schema={schema}
        sections={sections}
        defaultValues={defaultValues}
        submitConfig={{ type: 'default', hideSubmitButton: true }}
        onSubmit={onSubmit}
        styling={{ showSectionsSidepanel: false }}
      />
    </F0Box>
  )
}
